import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Link, useParams } from 'react-router-dom';
import ProductCard from './ProductCard';
import { ProductCardSkeleton } from './ProductCard';
import '../css/ProductDetails.css';
import ProductImageSlider from './ProductImageSlidder';
import useFetch from './useFetch';
import { domain } from './Domain';
import CartButton from './CartButton';
import useApiRequest from './useApiRequest';
import { useSessionId } from './SessionContext';
import { useLoginContext } from './LoginContext';
import { DNA } from 'react-loader-spinner';

const RELATED_LIMIT = 6;
const FETCH_BATCH = 80;

const ProductDetails = () => {
    const { slug } = useParams();
    const { data, loading, error } = useFetch(`${domain}/products/${slug}`);
    const [count, setCount] = useState(1);
    const [add, setAdding] = useState(false);
    const [related, setRelated] = useState([]);
    const [relatedLoading, setRelatedLoading] = useState(false);

    const { postRequest } = useApiRequest();
    const { sessionId, handleFetch } = useSessionId();
    const auth = useLoginContext();

    const getHeaders = useCallback(() => {
        const headers = {};
        if (auth.userInfo.token !== null) {
            headers.Authorization = `Token ${auth.userInfo.token}`;
        }
        return headers;
    }, [auth.userInfo.token]);

    useEffect(() => {
        setCount(1);
    }, [slug]);

    useEffect(() => {
        if (!data?.category) return undefined;

        const ac = new AbortController();
        setRelatedLoading(true);

        (async () => {
            try {
                const params = new URLSearchParams({
                    limit: String(FETCH_BATCH),
                    offset: '0',
                });
                const res = await fetch(`${domain}/products?${params}`, {
                    signal: ac.signal,
                    headers: getHeaders(),
                });
                if (!res.ok) throw new Error(res.statusText);
                const json = await res.json();
                const list = Array.isArray(json.results)
                    ? json.results
                    : Array.isArray(json)
                      ? json
                      : [];
                const sameCategory = list.filter(
                    (p) =>
                        p.id !== data.id &&
                        Number(p.category) === Number(data.category)
                );
                setRelated(sameCategory.slice(0, RELATED_LIMIT));
            } catch (e) {
                if (e.name !== 'AbortError') setRelated([]);
            } finally {
                setRelatedLoading(false);
            }
        })();

        return () => ac.abort();
    }, [data?.id, data?.category, getHeaders]);

    const handleAddToCart = async () => {
        if (!data || data.qty < 1) return;
        setAdding(true);
        try {
            const formData = new FormData();
            formData.append('id', slug);
            formData.append('session-id', sessionId);
            formData.append('quantity', String(count));
            const res = await postRequest(`${domain}/add-to-cart`, formData);
            if (res) handleFetch();
        } finally {
            setAdding(false);
        }
    };

    const handleCountChange = (e) => {
        const raw = Number.parseInt(e.target.value, 10);
        let next = Number.isFinite(raw) ? raw : 1;
        if (next < 1) next = 1;
        if (data && data.qty > 0) next = Math.min(next, data.qty);
        setCount(next);
    };

    const bumpCount = (delta) => {
        if (!data) return;
        setCount((c) => {
            let next = c + delta;
            if (next < 1) next = 1;
            if (data.qty > 0) next = Math.min(next, data.qty);
            return next;
        });
    };

    const bytesToMB = (bytes) => {
        if (bytes === 0) return '0 MB';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    const galleryImages = useMemo(() => {
        if (!data) return [];
        if (data.images?.length > 0) return data.images;
        if (data.image) return [data.image];
        return [];
    }, [data]);

    const inStock = data && data.qty > 0;
    const vatInclusive = data
        ? Math.round(data.selling_price * 1.18 * 100) / 100
        : 0;

    const orderCode =
        data?.item_code != null && String(data.item_code).trim() !== ''
            ? String(data.item_code).trim()
            : '';

    return (
        <div className="product-details-page">
            <Header />
            <main className="product-details-main container py-4 py-md-5">
                {loading && (
                    <div
                        className="product-details-loading card border-0 shadow-sm"
                        aria-busy="true"
                    >
                        <div className="card-body text-center py-5">
                            <DNA
                                visible
                                height="64"
                                width="64"
                                ariaLabel="Loading product"
                                wrapperClass="product-details-loading-dna"
                            />
                            <p className="text-secondary mb-0 mt-3">
                                Loading product…
                            </p>
                        </div>
                    </div>
                )}

                {!loading && error && (
                    <div
                        className="product-details-error card border-0 shadow-sm"
                        role="alert"
                    >
                        <div className="card-body py-5 px-4 text-center">
                            <h1 className="h5 mb-2">Couldn&apos;t load this product</h1>
                            <p className="text-secondary mb-4">
                                {error}. It may have been removed or the link is
                                invalid.
                            </p>
                            <Link to="/" className="btn btn-primary">
                                Back to catalog
                            </Link>
                        </div>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        <nav
                            className="product-details-breadcrumb mb-3"
                            aria-label="Breadcrumb"
                        >
                            <Link to="/">Home</Link>
                            <span className="product-details-bc-sep" aria-hidden>
                                /
                            </span>
                            <span className="text-secondary">
                                {data.product_name}
                            </span>
                        </nav>

                        <div className="product-details-hero card border-0 shadow-sm mb-4">
                            <div className="card-body p-3 p-md-4">
                                <div className="product-details-grid">
                                    <div className="product-details-gallery">
                                        <div className="product-details-gallery-card">
                                            <ProductImageSlider
                                                images={galleryImages}
                                            />
                                        </div>
                                    </div>
                                    <div className="product-details-buy">
                                        <p className="product-details-eyebrow">
                                            Product details
                                        </p>
                                        <h1 className="product-details-title">
                                            {data.product_name}
                                        </h1>
                                        <p className="product-details-part">
                                            Part no.{' '}
                                            <strong>{data.part_number}</strong>
                                        </p>

                                        <div
                                            className={`product-details-order-strip${
                                                orderCode
                                                    ? ''
                                                    : ' product-details-order-strip--empty'
                                            }`}
                                        >
                                            <div>
                                                <span className="product-details-order-label">
                                                    Order code
                                                </span>
                                                {!orderCode && (
                                                    <span className="product-details-order-hint">
                                                        Same as item code in
                                                        your catalog — quote this
                                                        when ordering.
                                                    </span>
                                                )}
                                            </div>
                                            <span
                                                className={`product-details-order-value${
                                                    orderCode
                                                        ? ''
                                                        : ' product-details-order-value--empty'
                                                }`}
                                            >
                                                {orderCode || 'Not assigned'}
                                            </span>
                                        </div>

                                        <div
                                            className="product-details-avail"
                                            aria-label="Availability"
                                        >
                                            <strong>Available to order</strong>
                                            <span className="product-details-avail-sub">
                                                {' '}
                                                · Delivery in Kampala area
                                            </span>
                                        </div>

                                        <div className="product-details-price-block">
                                            <div className="product-details-price-row">
                                                <div>
                                                    <span className="product-details-price-label">
                                                        Excl. VAT
                                                    </span>
                                                    <p className="product-details-price-value mb-0">
                                                        UGX{' '}
                                                        {data.selling_price.toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="text-md-end">
                                                    <span className="product-details-price-label">
                                                        Incl. 18% VAT
                                                    </span>
                                                    <p className="product-details-price-value mb-0">
                                                        UGX{' '}
                                                        {vatInclusive.toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="product-details-stock mb-0">
                                                {inStock ? (
                                                    <>
                                                        <span className="product-details-stock-dot" />
                                                        <strong>
                                                            {data.qty}
                                                        </strong>{' '}
                                                        in stock
                                                    </>
                                                ) : (
                                                    <span className="text-danger fw-semibold">
                                                        Currently out of stock
                                                    </span>
                                                )}
                                            </p>
                                        </div>

                                        <div className="product-details-price-break">
                                            <div className="product-details-price-break-head">
                                                <span>Price break</span>
                                                <span>Per unit (excl. VAT)</span>
                                            </div>
                                            <div className="product-details-price-break-row">
                                                <span>1+</span>
                                                <span className="product-details-price-break-amt">
                                                    UGX{' '}
                                                    {data.selling_price.toLocaleString()}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="product-details-meta">
                                            <p>
                                                <span className="text-muted">
                                                    Condition
                                                </span>
                                                <br />
                                                {data.condition}
                                            </p>
                                            <p>
                                                <span className="text-muted">
                                                    Brand
                                                </span>
                                                <br />
                                                {data.brand_name}
                                            </p>
                                        </div>

                                        <div className="product-details-actions">
                                            <span
                                                id="qty-label"
                                                className="product-details-qty-label"
                                            >
                                                Quantity
                                            </span>
                                            <div
                                                className="product-details-qty"
                                                role="group"
                                                aria-labelledby="qty-label"
                                            >
                                                <button
                                                    type="button"
                                                    className="product-details-qty-btn"
                                                    onClick={() => bumpCount(-1)}
                                                    disabled={!inStock || count <= 1}
                                                    aria-label="Decrease quantity"
                                                >
                                                    <i className="fa-solid fa-minus" />
                                                </button>
                                                <input
                                                    type="number"
                                                    className="product-details-qty-input"
                                                    min={1}
                                                    max={Math.max(1, data.qty)}
                                                    value={count}
                                                    onChange={handleCountChange}
                                                    disabled={!inStock}
                                                />
                                                <button
                                                    type="button"
                                                    className="product-details-qty-btn"
                                                    onClick={() => bumpCount(1)}
                                                    disabled={
                                                        !inStock ||
                                                        count >= data.qty
                                                    }
                                                    aria-label="Increase quantity"
                                                >
                                                    <i className="fa-solid fa-plus" />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                className="product-details-add-btn"
                                                onClick={handleAddToCart}
                                                disabled={!inStock || add}
                                            >
                                                {add
                                                    ? 'Adding…'
                                                    : 'Add to cart'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <section className="product-details-description card border-0 shadow-sm mb-4">
                            <div className="card-body p-3 p-md-4">
                                <h2 className="product-details-section-title">
                                    Description &amp; specifications
                                </h2>
                                <div
                                    className="product-details-html"
                                    dangerouslySetInnerHTML={{
                                        __html: data.details,
                                    }}
                                />
                                {data.datasheets?.length > 0 && (
                                    <div className="product-details-datasheets">
                                        <h3 className="product-details-datasheets-title">
                                            Manuals &amp; datasheets
                                        </h3>
                                        <ul className="product-details-datasheets-list list-unstyled mb-0">
                                            {data.datasheets.map((item) => (
                                                <li key={item.url}>
                                                    <a
                                                        href={domain + item.url}
                                                        className="product-details-datasheet-link"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <i
                                                            className="fa-solid fa-file-pdf text-danger me-2"
                                                            aria-hidden
                                                        />
                                                        {item.name.replace(
                                                            'datasheets/',
                                                            ''
                                                        )}{' '}
                                                        <span className="text-muted">
                                                            (
                                                            {bytesToMB(
                                                                item.size
                                                            )}
                                                            )
                                                        </span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="product-details-related">
                            <h2 className="product-details-related-title">
                                More in this category
                            </h2>
                            <div className="product-details-related-grid">
                                {relatedLoading ? (
                                    <>
                                        <div
                                            key="sk1"
                                            className="product-details-related-cell"
                                        >
                                            <ProductCardSkeleton />
                                        </div>
                                        <div
                                            key="sk2"
                                            className="product-details-related-cell"
                                        >
                                            <ProductCardSkeleton />
                                        </div>
                                        <div
                                            key="sk3"
                                            className="product-details-related-cell"
                                        >
                                            <ProductCardSkeleton />
                                        </div>
                                    </>
                                ) : related.length === 0 ? (
                                    <p className="text-secondary mb-0 product-details-related-empty">
                                        No other products in this category yet.
                                        <Link
                                            to="/"
                                            className="ms-2 fw-semibold"
                                        >
                                            Browse all
                                        </Link>
                                    </p>
                                ) : (
                                    related.map((product) => (
                                        <div
                                            key={product.id}
                                            className="product-details-related-cell"
                                        >
                                            <ProductCard
                                                id={product.id}
                                                name={product.product_name}
                                                partNumber={product.part_number}
                                                stockAmount={product.qty}
                                                price={product.selling_price}
                                                image={domain + product.image}
                                            />
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>
                    </>
                )}
            </main>
            <Footer />
            <CartButton />
        </div>
    );
};

export default ProductDetails;
